import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    addShape,
    updateShape,
    removeShapes,
    clearSelection,
    toggleSelectedId,
    setSelectedIds,
    updateSelectedShapes,
    setInProgress,
    clearInProgress,
} from '../../../../../store/slices/drawingSlice';

import {
    makePolygonFeature,
    makeLineFeature,
    makeRectFeature,
    makeCircleFeature,
    makeTextFeature,
    buildPreviewGeoJSON,
    translateGeometry,
    isNearStart,
    distance,
    defaultProps,
} from './drawingUtils';

import { SOURCES, LAYERS, drawingSourceRef } from './DrawingLayer';
import { clearItemSelection } from '../../../../../store/slices/imageOverlaySlice';
 
const setPreviewData  = (_map, geojson)       => drawingSourceRef.setPreviewData?.(geojson);
const setShapesOnly   = (shapes, selectedIds) => drawingSourceRef.setShapesOnly?.(shapes, selectedIds);
const setShapesFull   = (shapes, selectedIds) => drawingSourceRef.setShapesFull?.(shapes, selectedIds);

export default function useDrawingManager({
    activeTool,
    activeShape,
    strokeColor,
    strokeWidth,
    fillColor,
    
    
    fontFamily, 
    fontSize, 
    bold, 
    textAlign
}) {
    const dispatch   = useDispatch();
    const map        = useSelector((s) => s.map.mapContainer);
 
    const currentFloor = useSelector((s) => s.api.currentFloor);

    const shapes      = useSelector((s) => s.drawing.shapes);
    const selectedIds = useSelector((s) => s.drawing.selectedIds);
 
    const shapesRef       = useRef(shapes);
    const selectedIdsRef  = useRef(selectedIds);
    useEffect(() => { shapesRef.current      = shapes;      }, [shapes]);
    useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);
 
    const stateRef = useRef({});
    stateRef.current = { activeTool, activeShape, strokeColor, strokeWidth, fillColor, fontFamily, fontSize, bold, textAlign, shapes, selectedIds, currentFloor };
     
    const dragRef = useRef({ active: false, startLngLat: null, shapeIds: [], origGeometries: {} });
     
    const drawDragRef = useRef({ active: false, startLngLat: null });
     
    const setCursor = useCallback((cursor) => {
        if (map) map.getCanvas().style.cursor = cursor;
    }, [map]);
     
    const hitTest = useCallback((point) => {
        if (!map) return [];
         
        const bbox = [
            [point.x - 6, point.y - 6],
            [point.x + 6, point.y + 6],
        ];
        
        const features = map.queryRenderedFeatures(bbox, {
            layers: [
                ...drawingSourceRef.hitLayers,
                LAYERS.selectedFill, LAYERS.selectedStroke,
            ],
        });
         
        const ids = features
        .filter((f) => {
            const p = f.properties || {};
            return !('vertexIndex' in p) && !('edgeIndex' in p) && !p.preview;
        })
        .map((f) => String(f.id))
        .filter(Boolean);
        
        return [...new Set(ids)];
    }, [map]);
     
    useEffect(() => {
        dispatch(clearSelection());
    }, [activeTool, dispatch]);
     
    useEffect(() => {
        if (!map) return;
         
        const cleanups = [];
        const on = (event, handler, options) => {
            map.on(event, handler, options);
            cleanups.push(() => map.off(event, handler));
        };
        const onCanvas = (event, handler) => {
            map.getCanvas().addEventListener(event, handler);
            cleanups.push(() => map.getCanvas().removeEventListener(event, handler));
        };
         
        setCursor('default');
        dispatch(clearInProgress());
        setPreviewData(map, { type: 'FeatureCollection', features: [] });
         
        if (activeTool === 'pen') {
            setCursor('crosshair');
             
            const buildProps = () => {
                const { strokeColor, strokeWidth, fillColor, currentFloor } = stateRef.current;
                return defaultProps({ strokeColor, strokeWidth, fillColor, floorId: currentFloor?.enc_id });
            };
             
            if (activeShape === 'freehand') {
                let coords = [];
                
                const commitShape = (c) => {
                    const props = buildProps();
                    const shape = c.length >= 3
                        ? makePolygonFeature(c, props)
                        : makeLineFeature(c, props);
                    dispatch(addShape(shape));
                    dispatch(clearInProgress());
                    coords = [];
                    setPreviewData(map, { type: 'FeatureCollection', features: [] });
                };

                const handleClick = (e) => {
                    const { lng, lat } = e.lngLat;
                    const pt = [lng, lat];
  
                    if (coords.length >= 2 && isNearStart(map, pt, coords[0])) {
                        commitShape(coords);
                        return;
                    }

                    coords = [...coords, pt];
                    dispatch(setInProgress({ shapeType: 'freehand', coords }));
                };

                const handleDblClick = (e) => {
                    e.preventDefault();
                    const { lng, lat } = e.lngLat;
                    const pt = [lng, lat];

                    const base = coords.length >= 2 ? coords.slice(0, -2) : [];
                    if (base.length < 1) return;

                    const nearStart = base.length >= 3 && isNearStart(map, pt, base[0]);
                    const final = nearStart ? base : [...base, pt];

                    if (final.length >= 2) {
                        commitShape(final);
                    }
                };
                
                const handleMouseMove = (e) => {
                    if (coords.length === 0) return;
                    const mousePos = [e.lngLat.lng, e.lngLat.lat];
                    setPreviewData(map, buildPreviewGeoJSON({ shapeType: 'freehand', coords }, mousePos));
                };
                
                on('click', handleClick);
                on('dblclick', handleDblClick);
                on('mousemove', handleMouseMove);
                 
                const handleKey = (e) => {
                    if (e.key === 'Escape') {
                        coords = [];
                        dispatch(clearInProgress());
                        setPreviewData(map, { type: 'FeatureCollection', features: [] });
                    }
                };
                window.addEventListener('keydown', handleKey);
                cleanups.push(() => window.removeEventListener('keydown', handleKey));
            }
             
            if (activeShape === 'rect') {
                const handleMouseDown = (e) => {
                    map.dragPan.disable();
                    drawDragRef.current = { active: true, startLngLat: [e.lngLat.lng, e.lngLat.lat] };
                };
                
                const handleMouseMove = (e) => {
                    if (!drawDragRef.current.active) return;
                    const mousePos = [e.lngLat.lng, e.lngLat.lat]; 
                    setPreviewData(map, buildPreviewGeoJSON(
                        { shapeType: 'rect', coords: [drawDragRef.current.startLngLat] },
                        mousePos
                    ));
                };
                
                const handleMouseUp = (e) => {
                    if (!drawDragRef.current.active) return;
                    map.dragPan.enable();
                    const { startLngLat } = drawDragRef.current;
                    const end = [e.lngLat.lng, e.lngLat.lat];
                    drawDragRef.current = { active: false };
                    
                    if (distance(startLngLat, end) < 0.0000001) return;
                    const shape = makeRectFeature(startLngLat, end, {
                        ...stateRef.current.strokeColor && { strokeColor: stateRef.current.strokeColor },
                        strokeWidth: stateRef.current.strokeWidth,
                        fillColor:   stateRef.current.fillColor,
                        floorId:     stateRef.current.currentFloor?.enc_id,
                    });
                    
                    dispatch(addShape(shape));
                    setPreviewData(map, { type: 'FeatureCollection', features: [] });
                };
                
                on('mousedown', handleMouseDown);
                on('mousemove', handleMouseMove);
                on('mouseup', handleMouseUp);
            }
             
            if (activeShape === 'circle') {
                const handleMouseDown = (e) => {
                    map.dragPan.disable();
                    drawDragRef.current = { active: true, startLngLat: [e.lngLat.lng, e.lngLat.lat] };
                };
                
                const handleMouseMove = (e) => {
                    if (!drawDragRef.current.active) return;
                    const mousePos = [e.lngLat.lng, e.lngLat.lat];
                    setPreviewData(map, buildPreviewGeoJSON(
                        { shapeType: 'circle', coords: [drawDragRef.current.startLngLat] },
                        mousePos
                    ));
                };
                
                const handleMouseUp = (e) => {
                    if (!drawDragRef.current.active) return;
                    map.dragPan.enable();
                    const { startLngLat } = drawDragRef.current;
                    const end = [e.lngLat.lng, e.lngLat.lat];
                    drawDragRef.current = { active: false };
                    
                    const radius = distance(startLngLat, end);
                    if (radius < 0.0000001) return;
                    const shape = makeCircleFeature(startLngLat, radius, {
                        strokeColor: stateRef.current.strokeColor,
                        strokeWidth: stateRef.current.strokeWidth,
                        fillColor:   stateRef.current.fillColor,
                        floorId:     stateRef.current.currentFloor?.enc_id,
                    });
                    dispatch(addShape(shape));
                    setPreviewData(map, { type: 'FeatureCollection', features: [] });
                };
                
                on('mousedown', handleMouseDown);
                on('mousemove', handleMouseMove);
                on('mouseup', handleMouseUp);
            }
        }
         
        if (activeTool === 'highlighter') {
            setCursor('pointer');
            
            const handleClick = (e) => {
                const ids = hitTest(e.point);
                if (ids.length === 0) {
                    dispatch(clearSelection());
                    return;
                }
                
                const targetIds = e.originalEvent?.shiftKey
                ? null  
                : ids.slice(0, 1);
                
                if (e.originalEvent?.shiftKey) {
                    ids.forEach((id) => dispatch(toggleSelectedId(id)));
                } else {
                    dispatch(setSelectedIds(targetIds));
                }
                 
                const applyIds = e.originalEvent?.shiftKey ? ids : targetIds;
                const { strokeColor, strokeWidth, fillColor } = stateRef.current;
                applyIds.forEach((id) => {
                    dispatch(updateShape({
                        id,
                        properties: { strokeColor, strokeWidth, fillColor },
                    }));
                });
            };
            
            on('click', handleClick);
        }
         
        if (activeTool === 'text') {
            setCursor('text');
            
            const handleClick = (e) => {
                const { lng, lat } = e.lngLat;
 
                const canvas = map.getCanvas();
                const rect   = canvas.getBoundingClientRect();
                const pixel  = map.project([lng, lat]);

                const ta = document.createElement('textarea');
                ta.placeholder = 'Type text… (Ctrl+Enter to confirm)';
                ta.rows        = 1;
                Object.assign(ta.style, {
                    position:   'fixed',
                    left:       `${rect.left + pixel.x}px`,
                    top:        `${rect.top  + pixel.y}px`,
                    zIndex:     9999,
                    fontSize:   `${stateRef.current.fontSize || 14}px`,
                    fontFamily: stateRef.current.fontFamily  || 'Arial',
                    fontWeight: stateRef.current.bold ? 'bold' : 'normal',
                    border:     '1px dashed #1a73e8',
                    background: 'rgba(255,255,255,0.9)',
                    padding:    '2px 6px',
                    minWidth:   '120px',
                    outline:    'none',
                    resize:     'none',
                    overflow:   'hidden',
                    lineHeight: '1.2',
                });

                document.body.appendChild(ta);
                ta.focus();
 
                const autoResize = () => {
                    ta.style.height = 'auto';
                    ta.style.height = ta.scrollHeight + 'px';
                };
                ta.addEventListener('input', autoResize);

                let committed = false;
                const commit = () => {
                    if (committed) return;
                    committed = true;
                    const text = ta.value;  
                    if (text.trim()) {
                        const shape = makeTextFeature([lng, lat], text, {
                            strokeColor: stateRef.current.strokeColor,
                            fontFamily:  stateRef.current.fontFamily,
                            fontSize:    stateRef.current.fontSize,
                            bold:        stateRef.current.bold,
                            textAlign:   stateRef.current.textAlign,
                            floorId:     stateRef.current.currentFloor?.enc_id,
                        });
                        dispatch(addShape(shape));
                    }
                    ta.remove();
                };

                ta.addEventListener('blur', commit);
                ta.addEventListener('keydown', (ev) => { 
                    if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) {
                        ev.preventDefault();
                        commit();
                    } 
                    if (ev.key === 'Escape') {
                        committed = true;  
                        ta.remove();
                    } 
                });
            };
            
            on('click', handleClick);
        }
         
        if (activeTool === 'eraser') {
            setCursor('crosshair');

            const drag = { active: false, moved: false, startPoint: null, startLngLat: null };
 
            const handleMouseDown = (e) => {
                if (e.button !== 0) return;
                map.dragPan.disable();
                const rect   = map.getCanvas().getBoundingClientRect();
                const point  = { x: e.clientX - rect.left, y: e.clientY - rect.top };
                const lngLat = map.unproject(point);
                drag.active      = true;
                drag.moved       = false;
                drag.startPoint  = point;
                drag.startLngLat = [lngLat.lng, lngLat.lat];
            };
 
            const handleWindowMouseMove = (e) => {
                if (!drag.active) return;
                const rect  = map.getCanvas().getBoundingClientRect();
                const point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
                const dx = Math.abs(point.x - drag.startPoint.x);
                const dy = Math.abs(point.y - drag.startPoint.y);
                if (dx > 4 || dy > 4) {
                    drag.moved   = true;
                    const lngLat = map.unproject(point);
                    setPreviewData(map, buildPreviewGeoJSON(
                        { shapeType: 'rect', coords: [drag.startLngLat] },
                        [lngLat.lng, lngLat.lat]
                    ));
                }
            };

            const handleWindowMouseUp = (e) => {
                if (!drag.active) return;
                map.dragPan.enable();
                drag.active = false;

                if (drag.moved) {
                    const rect = map.getCanvas().getBoundingClientRect();
                    const ep   = { x: e.clientX - rect.left, y: e.clientY - rect.top };
                    const sp   = drag.startPoint;
                    const bbox = [
                        [Math.min(sp.x, ep.x), Math.min(sp.y, ep.y)],
                        [Math.max(sp.x, ep.x), Math.max(sp.y, ep.y)],
                    ];
                    const features = map.queryRenderedFeatures(bbox, {
                        layers: [...drawingSourceRef.hitLayers, LAYERS.selectedFill, LAYERS.selectedStroke],
                    });
                    const ids = [...new Set(
                        features
                            .filter((f) => {
                                const p = f.properties || {};
                                return !('vertexIndex' in p) && !('edgeIndex' in p) && !p.preview;
                            })
                            .map((f) => String(f.id))
                            .filter(Boolean)
                    )];
                    
                    if (ids.length > 0) dispatch(setSelectedIds(ids));
                    setPreviewData(map, { type: 'FeatureCollection', features: [] });
                    
                    setTimeout(() => { drag.moved = false; }, 10);
                }
            };

            const handleClick = (e) => {
                if (drag.moved) return;
                const ids = hitTest(e.point);
                if (ids.length > 0) {
                    const { selectedIds } = stateRef.current;
                    const toDelete = selectedIds.length > 0 ? selectedIds : ids.slice(0, 1);
                    dispatch(removeShapes(toDelete));
                }
            };

            const handleKey = (e) => {
                if (e.key === 'Delete' || e.key === 'Backspace') {
                    const { selectedIds } = stateRef.current;
                    if (selectedIds.length > 0) dispatch(removeShapes(selectedIds));
                }
            };

            const canvas = map.getCanvas();
            canvas.addEventListener('mousedown', handleMouseDown, true);
            window.addEventListener('mousemove', handleWindowMouseMove);
            window.addEventListener('mouseup',   handleWindowMouseUp);
            window.addEventListener('keydown',   handleKey);
            cleanups.push(() => {
                canvas.removeEventListener('mousedown', handleMouseDown, true);
                window.removeEventListener('mousemove', handleWindowMouseMove);
                window.removeEventListener('mouseup',   handleWindowMouseUp);
                window.removeEventListener('keydown',   handleKey);
            });

            on('click', handleClick);
        }
         
        if (activeTool === 'select') {
            setCursor('default');
             
            const vertexDragRef = { active: false, parentId: null, vertexIndex: null };
             
            const vertexHitTest = (point) => {
                const bbox = [
                    [point.x - 8, point.y - 8],
                    [point.x + 8, point.y + 8],
                ];
                const features = map.queryRenderedFeatures(bbox, {
                    layers: [LAYERS.vertices],
                });
                if (features.length === 0) return null;
                const f = features[0];
                return {
                    parentId:    String(f.properties.parentId),
                    vertexIndex: Number(f.properties.vertexIndex),
                };
            };
              
            const isNearTextShape = (point) => {
                const { shapes, currentFloor } = stateRef.current;
                const floorId = currentFloor?.enc_id ?? null;
                return shapes.some((s) => {
                    if (s.properties?.shapeType !== 'text') return false;
                    if (s.properties?.floorId !== floorId)  return false;
                    const px = map.project(s.geometry.coordinates);
                    return Math.hypot(px.x - point.x, px.y - point.y) < 50;
                });
            };

            const handleClick = (e) => {
                if (dragRef.current.moved) return;
                if (vertexDragRef.active)  return;

                const ids = hitTest(e.point);
                if (ids.length === 0) { 
                    if (isNearTextShape(e.point)) return;
                    dispatch(clearSelection());
                    return;
                }
                if (e.originalEvent?.shiftKey) {
                    ids.forEach((id) => dispatch(toggleSelectedId(id)));
                } else {
                    dispatch(setSelectedIds(ids.slice(0, 1)));
                }
                dispatch(clearItemSelection());
            };

            const handleMouseDown = (e) => {
                const vertex = vertexHitTest(e.point);
                if (vertex) {
                    vertexDragRef.active      = true;
                    vertexDragRef.parentId    = vertex.parentId;
                    vertexDragRef.vertexIndex = vertex.vertexIndex;
                    map.dragPan.disable();
                    setCursor('crosshair');
                    return;
                }

                const ids = hitTest(e.point);
                if (ids.length === 0) { 
                    return;
                }
                
                const { selectedIds, shapes } = stateRef.current;
                const activeIds =
                selectedIds.length > 0 && selectedIds.some((id) => ids.includes(id))
                ? selectedIds
                : ids.slice(0, 1);
                
                const origGeometries = {};
                activeIds.forEach((id) => {
                    const shape = shapes.find((s) => s.id === id);
                    if (shape) origGeometries[id] = JSON.parse(JSON.stringify(shape.geometry));
                });
                
                dragRef.current = {
                    active:        true,
                    moved:         false,
                    startLngLat:   [e.lngLat.lng, e.lngLat.lat],
                    shapeIds:      activeIds,
                    origGeometries,
                };
                
                map.dragPan.disable();
            };
             
            const handleMouseMove = (e) => {
                const lngLat = [e.lngLat.lng, e.lngLat.lat];
                 
                if (vertexDragRef.active) {
                    const { parentId, vertexIndex } = vertexDragRef;
                    const { shapes } = stateRef.current;
                    const shape = shapes.find((s) => s.id === parentId);
                    if (!shape || shape.geometry.type !== 'Polygon') return;
                     
                    const coords = shape.geometry.coordinates[0].map((c) => [...c]);
                    coords[vertexIndex] = lngLat;
                     
                    if (vertexIndex === 0) {
                        coords[coords.length - 1] = lngLat;
                    } 
                    if (vertexIndex === coords.length - 1) {
                        coords[0] = lngLat;
                    }
                     
                    const newGeometry   = { ...shape.geometry, coordinates: [coords] };
                    const floorId       = stateRef.current.currentFloor?.enc_id ?? null;
                    const updatedShapes = shapesRef.current
                        .map((s) => s.id === parentId ? { ...s, geometry: newGeometry } : s)
                        .filter((s) => s.properties?.floorId === floorId);
                    drawingSourceRef.isDragging = true;
                    setShapesOnly(updatedShapes, selectedIdsRef.current);
                    vertexDragRef.liveGeometry = { id: parentId, geometry: newGeometry };
                    return;
                }
                 
                if (dragRef.current.active) {
                    dragRef.current.moved = true;
                    const { startLngLat, shapeIds, origGeometries } = dragRef.current;
                    const dlng = lngLat[0] - startLngLat[0];
                    const dlat = lngLat[1] - startLngLat[1];
                     
                    const geomUpdates = {};
                    shapeIds.forEach((id) => {
                        const orig = origGeometries[id];
                        if (!orig) return;
                        geomUpdates[id] = translateGeometry(orig, dlng, dlat);
                    });
                    const floorId       = stateRef.current.currentFloor?.enc_id ?? null;
                    const updatedShapes = shapesRef.current
                        .map((s) => geomUpdates[s.id] ? { ...s, geometry: geomUpdates[s.id] } : s)
                        .filter((s) => s.properties?.floorId === floorId);
                    drawingSourceRef.isDragging = true;
                    setShapesOnly(updatedShapes, selectedIdsRef.current);
                    dragRef.current.liveGeomUpdates = geomUpdates;
                    return;
                }
                 
                const vertex = vertexHitTest(e.point);
                if (vertex) {
                    setCursor('crosshair');
                    return;
                }
                const ids = hitTest(e.point);
                setCursor(ids.length > 0 ? 'move' : 'default');
            };
             
            const handleMouseUp = () => { 
                drawingSourceRef.isDragging = false;
                 
                if (vertexDragRef.active) {
                    if (vertexDragRef.liveGeometry) {
                        dispatch(updateShape(vertexDragRef.liveGeometry));
                    }
                    vertexDragRef.active       = false;
                    vertexDragRef.parentId     = null;
                    vertexDragRef.vertexIndex  = null;
                    vertexDragRef.liveGeometry = null;
                    map.dragPan.enable();
                    setCursor('default');
                    return;
                } 
                if (dragRef.current.active) {
                    const { liveGeomUpdates } = dragRef.current;
                    if (liveGeomUpdates) {
                        Object.entries(liveGeomUpdates).forEach(([id, geometry]) => {
                            dispatch(updateShape({ id, geometry }));
                        });
                    }
                    map.dragPan.enable();
                    setTimeout(() => {
                        dragRef.current = { active: false, moved: false, liveGeomUpdates: null };
                        drawingSourceRef.isDragging = false;
                    }, 10);
                }
            };
            
            on('click',     handleClick);
            on('mousedown', handleMouseDown);
            on('mousemove', handleMouseMove);
            on('mouseup',   handleMouseUp);
             
            const handleKey = (e) => {
                if (e.key === 'Delete' || e.key === 'Backspace') {
                    const { selectedIds } = stateRef.current;
                    if (selectedIds.length > 0) dispatch(removeShapes(selectedIds));
                }
            };
            window.addEventListener('keydown', handleKey);
            cleanups.push(() => window.removeEventListener('keydown', handleKey));
        }
         
        return () => {
            cleanups.forEach((fn) => fn()); 
            drawingSourceRef.isDragging = false;
        };
    }, [map, activeTool, activeShape, dispatch, hitTest, setCursor]);
     
    const prevToolbarRef = useRef({});
    useEffect(() => {
        const prev = prevToolbarRef.current;
 
        const changed = {};
        if (strokeColor !== prev.strokeColor) changed.strokeColor = strokeColor;
        if (strokeWidth !== prev.strokeWidth) changed.strokeWidth = strokeWidth;
        if (fillColor   !== prev.fillColor)   changed.fillColor   = fillColor;
        if (fontFamily  !== prev.fontFamily)  changed.fontFamily  = fontFamily;
        if (fontSize    !== prev.fontSize)    changed.fontSize    = fontSize;
        if (bold        !== prev.bold)        changed.bold        = bold;
        if (textAlign   !== prev.textAlign)   changed.textAlign   = textAlign;
 
        prevToolbarRef.current = { strokeColor, strokeWidth, fillColor, fontFamily, fontSize, bold, textAlign };

        if (selectedIds.length === 0) return;
        if (Object.keys(changed).length > 0) {
            dispatch(updateSelectedShapes(changed));
        }
    }, [strokeColor, strokeWidth, fillColor, fontFamily, fontSize, bold, textAlign]);
     
    useEffect(() => {
        if (selectedIds.length === 0) return;
        const { shapes } = stateRef.current;
        const shape = shapes.find((s) => s.id === selectedIds[0]);
        if (!shape) return;
        const p = shape.properties;
         
        const updates = [
            ['strokeColor', p.strokeColor],
            ['strokeWidth', p.strokeWidth],
            ['fillColor',   p.fillColor],
            ['fontFamily',  p.fontFamily],
            ['fontSize',    p.fontSize],
            ['bold',        p.bold],
            ['textAlign',   p.textAlign],
        ].filter(([, v]) => v !== undefined);
        
        updates.forEach(([key, value]) => {
            dispatch({ type: `drawingToolbar/set${key.charAt(0).toUpperCase() + key.slice(1)}`, payload: value });
        });
    }, [selectedIds]);
     
    const inProgress = useSelector((s) => s.drawing.inProgress);
    useEffect(() => {
        stateRef.current.inProgress = inProgress;
    }, [inProgress]);
     
}